import routes from 'js/core_pre_webpack/routes';

// The one file under profiles/autotest allowed to import the generated routes
// helper, because the helper escapes nothing. relativeRoute in
// core_pre_webpack/routes.js pushes path params straight into the segment list
// and builds the query as `${key}=${value}`, so &, ?, #, a space or any
// non-ASCII character lands in the URL verbatim - in the path as much as in the
// query. Every string below is encoded on the way in, exactly once, so an arm
// gets a finished URL and never has to know any of this.
//
// Two further traps in that helper, both handled by only ever passing it a
// fresh object literal with keys that are actually set: it DELETES the keys it
// consumes off the object it was handed (and writes subdomain_param back onto
// it), and an undefined value survives interpolation as the literal text
// "undefined" rather than dropping out.

export function workEditUrl(workId: number): string {
  return routes.work_edit_url({ id: workId });
}

export function uploadDocumentUrl(source: string): string {
  return routes.upload_funnel_document_url({
    source: encodeURIComponent(source),
  });
}

export function profileEditUrl(pageName: string): string {
  return routes.edit_new_profile_url({
    page_name: encodeURIComponent(pageName),
  });
}

// The profile's CV and Network sub-pages.
export function cvUrl(pageName: string): string {
  return routes.new_profile_curriculum_vitae_url({
    page_name: encodeURIComponent(pageName),
  });
}

export function networkUrl(pageName: string): string {
  return routes.network_new_profile_url({
    page_name: encodeURIComponent(pageName),
  });
}

// The mentions and citations sub-pages have no helper to call: JsGenerators::
// Routes allow-lists controllers, and mentions#public_index / #public_citations_
// index are not on it, so routes.js never carries them. They ride the CV
// route's base instead — the one sibling that does have a helper — exactly as
// the analytics client views ride analyticsBase, so page_name is still encoded
// by the helper and never spelled here.
function profileBase(pageName: string): string {
  return cvUrl(pageName).replace(/\/CurriculumVitae$/, '');
}

export function mentionsUrl(pageName: string): string {
  return `${profileBase(pageName)}/mentions`;
}

export function citationsUrl(pageName: string): string {
  return `${profileBase(pageName)}/citations`;
}

// Rails routes the dashboard as ":page_name/Analytics(/*react_path)" - one
// route with a glob, so the sub-views are client routes with no helper of their
// own and the helper only ever yields the base. Concatenating the view is what
// Navbar and the cancel-premium cards do too.
const ANALYTICS_VIEWS = {
  readers: '/activity/readers',
  countries: '/activity/countries',
} as const;

export type AnalyticsView = keyof typeof ANALYTICS_VIEWS;

// subdomain is the one argument here that is neither a path segment nor a query
// value: absoluteRoute splices it into the host, where percent-encoding buys
// nothing and a '/' would move the link to another origin entirely. Anything
// that is not a plain host label is dropped, which leaves the helper on its own
// default of www.
const HOST_LABEL = /^[a-z\d][a-z\d.-]*$/i;

function analyticsBase(pageName: string, subdomain?: string): string {
  return routes.analytics_dashboard_url({
    page_name: encodeURIComponent(pageName),
    ...(subdomain != null && HOST_LABEL.test(subdomain) ? { subdomain } : {}),
  });
}

export function analyticsDashboardUrl(
  pageName: string,
  view: AnalyticsView,
  subdomain?: string,
): string {
  return `${analyticsBase(pageName, subdomain)}${ANALYTICS_VIEWS[view]}`;
}

// The dashboard's per-work deep link, another client route under the same
// glob: control's premium view-impact row concatenates
// '/activity/documents/<id>' onto the dashboard base exactly as the named
// views above are.
export function analyticsDocumentUrl(
  pageName: string,
  workId: number,
  subdomain?: string,
): string {
  return `${analyticsBase(pageName, subdomain)}/activity/documents/${workId}`;
}

// Where control sends a free owner who asks for a premium analytics view: the
// upgrade interstitial, keyed by which feature earned the visit and which
// surface sent it - the same two query values the Navbar and the work strip
// pass. Both are our own fixed strings today, but they ride the raw query like
// everything else here, so they are encoded all the same. The work strip's
// bookmark and mention rows also carry after_upgrade_work_id - the work to
// come back to once checkout ends - spread in only when given, because an
// undefined value would ride the query as the literal text "undefined".
export function upgradeUrl(
  feature: string,
  trigger: string,
  afterUpgradeWorkId?: number,
): string {
  return routes.upgrade_url({
    feature: encodeURIComponent(feature),
    trigger: encodeURIComponent(trigger),
    ...(afterUpgradeWorkId != null
      ? { after_upgrade_work_id: afterUpgradeWorkId }
      : {}),
  });
}

// The four API destinations are relative paths, fetched same-origin. That only
// reaches an api-constrained route because SubdomainParamMiddleware reads
// subdomain_param off the query and rewrites the request host before routing;
// drop it and the request 404s. The helper would add it itself for these
// routes, but passing it fixes where it sits in the query string.
const API = 'api';

export function researchInterestWorksPath(
  interestId: number,
  pageSize: number,
): string {
  return routes.works_api_v0_research_interest_path({
    subdomain_param: API,
    id: interestId,
    page_size: pageSize,
  });
}

export function hitsByCountryPath(userId: number): string {
  return routes.all_time_hits_by_country_api_v0_user_stats_path({
    subdomain_param: API,
    user_id: userId,
  });
}

export function universityHitCountsPath(userId: number): string {
  return routes.university_hit_counts_api_v0_user_stats_path({
    subdomain_param: API,
    user_id: userId,
  });
}

// This route has no path components of its own, so all four values land in the
// query - page 0 included, which survives because the helper builds the query
// off Object.entries rather than off truthiness. Which page and how many per
// page is pageData's policy, not this file's.
export function profileWorksJsonPath(
  userId: number,
  page: number,
  perPage: number,
): string {
  return routes.api_v0_profiles_works_json_index_path({
    subdomain_param: API,
    user_id: userId,
    page,
    per_page: perPage,
  });
}

// The one destination whose parameter is a list, and the one place the helper's
// query building cannot be used as intended: relativeRoute renders a single
// `key=value` per entry off Object.entries, while
// Api::V0::SocialDistancesController calls #map on params[:work_ids] and so
// needs the bracketed key repeated. Handed the array itself the helper emits
// one comma-joined value, which raises there rather than 404s - the other batch
// endpoints split a comma list, this one does not. Pre-joining also leaves the
// helper owning the sole '?': the page's own socialDistanceFetcher concatenates
// a second one onto a finished URL, SubdomainParamMiddleware then reads
// "api?work_ids[]=1" as the subdomain, and the whole batch 404s.
export function socialDistancePath(workIds: number[]): string {
  return routes.api_v0_social_distance_path({
    subdomain_param: API,
    'work_ids[]': workIds.join('&work_ids[]='),
  });
}

export function searchPath(query: string): string {
  return routes.search_path({ q: encodeURIComponent(query) });
}
